import { FC, useEffect, useRef, useState } from "react";
import { Button } from "../../components";
import { DownloadIcon } from "../../assets/icons";
import {
  useExportDisbursedInvoices,
  useExportLoanPaymentSchedule,
} from "../../hooks";
import { DocumentExportFormat } from "../../hooks/api/types";

type DownloadDocumentMenuProps = {
  loanApplicationId: string;
  kind: "repayment-schedule" | "disbursed-invoices";
};

const FORMAT_OPTIONS: {
  format: DocumentExportFormat;
  label: string;
  dotClass: string;
}[] = [
  {
    format: DocumentExportFormat.XLSX,
    label: "XLSX file",
    dotClass: "cx-bg-green-500",
  },
  {
    format: DocumentExportFormat.PDF,
    label: "PDF file",
    dotClass: "cx-bg-red-500",
  },
];

export const DownloadDocumentMenu: FC<DownloadDocumentMenuProps> = ({
  loanApplicationId,
  kind,
}) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingFormat, setLoadingFormat] =
    useState<DocumentExportFormat | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const scheduleExport = useExportLoanPaymentSchedule();
  const invoicesExport = useExportDisbursedInvoices();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (loadingFormat != null) return;
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [loadingFormat]);

  const handleDownload = async (format: DocumentExportFormat) => {
    setError(null);
    setLoadingFormat(format);
    try {
      if (kind === "repayment-schedule") {
        await scheduleExport.mutateAsync({ loanApplicationId, format });
      } else {
        await invoicesExport.mutateAsync({ loanApplicationId, format });
      }
      setOpen(false);
    } catch {
      setError("Download failed. Please try again.");
      setOpen(true);
    } finally {
      setLoadingFormat(null);
    }
  };

  const isLoading = loadingFormat != null;

  return (
    <div className="cx-relative" ref={containerRef}>
      <Button
        slim={true}
        disabled={isLoading || !loanApplicationId}
        onClick={() => {
          if (open) setError(null);
          setOpen((prev) => !prev);
        }}
        label={
          <div className="cx-flex cx-flex-row cx-items-center cx-gap-x-2">
            <DownloadIcon />
            <div className="cx-interaction-text-inverse cx-font-semibold cx-text-sm">
              {isLoading ? "Downloading…" : "Download"}
            </div>
          </div>
        }
      />
      {open && (
        <div className="cx-absolute cx-right-0 cx-mt-2 cx-z-20 cx-w-48 cx-rounded-xl cx-border cx-border-stroke-primary cx-bg-white cx-shadow-lg cx-p-2">
          <div className="cx-text-text-secondary cx-text-xs cx-font-medium cx-px-2 cx-py-1">
            Select file format
          </div>
          {FORMAT_OPTIONS.map((option) => {
            const optionLoading = loadingFormat === option.format;
            return (
              <button
                key={option.format}
                type="button"
                disabled={isLoading}
                onClick={() => handleDownload(option.format)}
                className="cx-w-full cx-flex cx-items-center cx-gap-2 cx-rounded-lg cx-px-2 cx-py-2 cx-text-left cx-text-sm cx-text-text-primary hover:cx-bg-background-secondary disabled:cx-opacity-60"
              >
                <span
                  className={`cx-inline-block cx-w-2 cx-h-2 cx-rounded-full ${option.dotClass}`}
                />
                <span className="cx-font-medium">
                  {optionLoading ? "Downloading…" : option.label}
                </span>
              </button>
            );
          })}
          {error && (
            <div className="cx-text-red-600 cx-text-xs cx-px-2 cx-pt-1">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
